// File: webelieve-frontend/src/features/intake/components/MetricsOverview.tsx
// M7.30: Refactored to show supervisory operational posture instead of generic metrics.

import React from 'react';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ShieldExclamationIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import type { SupervisoryPosture } from '../api/edgeflowApi';

interface MetricsOverviewProps {
  supervisory: SupervisoryPosture;
  onRefresh: () => void;
  onQueueClick?: (queueName: string) => void;
}

const MetricsOverview: React.FC<MetricsOverviewProps> = ({ supervisory, onRefresh, onQueueClick }) => {
  // Each card maps to exactly one backend queue so the drill-down cohort
  // count always matches what the card displays. No aggregate cards.
  const allCards = [
    {
      title: 'Active Leads',
      value: supervisory.total_active_leads,
      icon: UserGroupIcon,
      color: 'blue',
      subtitle: supervisory.stage_bottleneck
        ? `Bottleneck: ${supervisory.stage_bottleneck} (${supervisory.stage_bottleneck_count})`
        : 'No bottleneck detected',
    },
    {
      title: 'High-Urgency Unreviewed',
      value: supervisory.high_urgency_unreviewed_count,
      icon: ExclamationTriangleIcon,
      color: supervisory.high_urgency_unreviewed_count > 0 ? 'red' : 'green',
      subtitle: supervisory.high_urgency_unreviewed_count > 0 ? 'Immediate attention needed' : 'None',
      queue: 'high_urgency_unreviewed',
    },
    {
      title: 'Review Required',
      value: supervisory.review_required_count,
      icon: EyeIcon,
      color: supervisory.review_required_count > 0 ? 'yellow' : 'green',
      subtitle: 'Early-stage leads with submissions',
      queue: 'review_required',
    },
    {
      title: 'Stuck in Stage',
      value: supervisory.stuck_leads_count,
      icon: ClockIcon,
      color: supervisory.stuck_leads_count > 0 ? 'yellow' : 'green',
      subtitle: 'Leads exceeding stage time threshold',
      queue: 'stuck_in_stage',
    },
    {
      title: 'Stale Leads',
      value: supervisory.stale_leads_count,
      icon: ClockIcon,
      color: supervisory.stale_leads_count > 0 ? 'yellow' : 'green',
      subtitle: 'No recent activity',
      queue: 'stale_leads',
    },
    {
      title: 'Missing Conflict Checks',
      value: supervisory.missing_conflict_check_count,
      icon: ShieldExclamationIcon,
      color: supervisory.missing_conflict_check_count > 0 ? 'yellow' : 'green',
      subtitle: 'In qualifying stage without check',
      queue: 'missing_conflict_check',
    },
    {
      title: 'Missing Documents',
      value: supervisory.missing_documents_count,
      icon: DocumentTextIcon,
      color: supervisory.missing_documents_count > 0 ? 'yellow' : 'green',
      subtitle: 'No uploaded intake documents',
      queue: 'missing_documents',
    },
    {
      title: 'Unsigned Retainers',
      value: supervisory.retainer_sent_unsigned_count,
      icon: DocumentTextIcon,
      color: supervisory.retainer_sent_unsigned_count > 0 ? 'yellow' : 'green',
      subtitle: supervisory.retainer_sent_unsigned_count > 0 ? 'Follow-up needed' : 'All signed or not sent',
      queue: 'retainer_unsigned',
    },
    {
      title: 'Workflow Failures',
      value: supervisory.unresolved_workflow_failures_count,
      icon: ExclamationTriangleIcon,
      color: supervisory.unresolved_workflow_failures_count > 0 ? 'red' : 'green',
      subtitle: supervisory.unresolved_workflow_failures_count > 0 ? 'Needs review' : 'All clear',
      queue: 'workflow_failures',
    },
  ];

  // Only show cards with non-zero counts (except Active Leads which always shows)
  const posturCards = allCards.filter(card => !card.queue || card.value > 0);

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-lex-accent-subtle text-lex-accent border-lex-accent/20',
      green: 'bg-lex-success-bg text-lex-success border-lex-success/20',
      yellow: 'bg-lex-warning-bg text-lex-warning border-lex-warning/20',
      red: 'bg-lex-danger-bg text-lex-danger border-lex-danger/20',
      gray: 'bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Operational Posture
        </h2>
        <button onClick={onRefresh} className="text-sm text-lex-accent hover:text-lex-accent-hover flex items-center gap-1">
          <ArrowPathIcon className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {posturCards.map((card, idx) => (
          <button
            key={idx}
            onClick={() => card.queue && onQueueClick?.(card.queue)}
            disabled={!card.queue}
            className={`p-4 rounded-lg border text-left transition-all duration-200 hover:shadow-md ${getColorClasses(card.color)} ${card.queue ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <card.icon className="w-5 h-5 mr-3" />
                <div>
                  <p className="text-sm font-medium opacity-90">{card.title}</p>
                  <p className="text-2xl font-bold">{card.value}</p>
                </div>
              </div>
            </div>
            <p className="text-xs opacity-75 mt-2">{card.subtitle}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MetricsOverview;
